import { useCallback, useMemo, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { createColumnLabelResolver } from '@features/event-level-column-rename/utils/resolve-column-label';
import type {
  ColumnLabelResolver,
  LineItemColumnLabels,
} from '@features/event-level-column-rename/types';

/**
 * Memoised label resolver for a single event's label map.
 *
 * The stable identity is a correctness requirement, not a micro-optimisation: grid column
 * settings and header callbacks are hot paths that hold the resolver in a dependency array,
 * and a fresh function each render rebuilds every column on every keystroke.
 *
 * `useTranslation` hands back a new `t` on every render, so the resolver reads translations
 * through a ref instead. That keeps the returned identity keyed on the label map alone — which
 * is the only input that can change what a column is called.
 *
 * Pass the map straight off the event payload: its own identity only changes when the event
 * reloads, which is precisely when labels can have changed.
 */
export const useColumnLabelResolver = (labels?: LineItemColumnLabels | null): ColumnLabelResolver => {
  const { t } = useTranslation('common');
  const translateRef = useRef(t);

  translateRef.current = t;

  const translate = useCallback((i18nKey: string) => translateRef.current(i18nKey), []);

  return useMemo(() => createColumnLabelResolver({ labels, t: translate }), [labels, translate]);
};
